import type { Experience as ApiExperience } from '@/shared/api/generated/api';
import { experienceCityUrl, experienceUrl } from '@/shared/helpers/urls';

import type { Experience as ExpCardExperience } from '@experience/exp-card/dist/types/experience';

type GuideAvatarPayload = {
  readonly medium?: string;
  readonly small?: string;
};

type GuidePayload = Omit<ApiExperience['guide'], 'avatar' | 'guide_type'> & {
  readonly avatar?: string | GuideAvatarPayload;
  readonly guide_type?: ApiExperience['guide']['guide_type'];
  readonly type?: string;
};

export type ExperiencePayload = Omit<ApiExperience, 'guide'> & {
  readonly guide: GuidePayload;
};

export type MovementTypeLabels = Readonly<Record<string, string>>;

// Приводит оба формата аватарки из API к контракту redesign-карточки.
const mapGuideAvatar = (avatar: GuidePayload['avatar']) => {
  if (!avatar) {
    return undefined;
  }

  if (typeof avatar === 'string') {
    return { medium: avatar, small: avatar };
  }

  return {
    medium: avatar.medium ?? avatar.small ?? '',
    small: avatar.small ?? avatar.medium ?? '',
  };
};

// API главной и exp-card используют разные версии контракта экскурсии. Адаптер
// заполняет обязательные поля redesign-компонента, не меняя исходные данные API.
export const mapExperienceToExpCard = (
  exp: ExperiencePayload,
  movementTypeLabels: MovementTypeLabels = {},
): ExpCardExperience => {
  const price = exp.price ?? {
    currency: 'RUB',
    currency_rate: 1,
    price_from: false,
    unit_string: '',
    value: 0,
    value_string: '0 ₽',
  };

  return {
    id: exp.id,
    title: exp.title,
    text: exp.tagline,
    tagline: exp.tagline,
    annotation: exp.tagline,
    url: exp.url ?? experienceUrl(exp.id),
    type: exp.type,
    format: exp.format,
    grade: exp.grade,
    movement_type: movementTypeLabels[exp.movement_type] ?? '',
    schedule_type: exp.schedule_type,
    status: exp.status ?? 'active',
    is_instant_booking: exp.instant_booking ?? false,
    is_new: false,
    show_promotion_badge: false,
    max_persons: exp.max_persons ?? 0,
    duration: exp.duration ?? 0,
    price,
    display_price: exp.display_price,
    rating: exp.rating ?? 0,
    review_count: exp.review_count ?? 0,
    geo: {
      city: exp.geo?.city?.map((city) => ({
        id: city.id,
        slug: city.slug,
        name: city.name,
        name_ru: city.name,
        in_obj_phrase: city.in_obj_phrase,
        from_obj_phrase: city.from_obj_phrase,
        across_obj_phrase: city.across_obj_phrase,
        url: experienceCityUrl(city.slug),
      })),
      country: exp.geo?.country?.map((country) => ({
        id: country.id,
        slug: country.slug,
        name: country.name,
        in_obj_phrase: country.in_obj_phrase,
        from_obj_phrase: country.from_obj_phrase,
        across_obj_phrase: country.across_obj_phrase,
      })),
    },
    guide: {
      id: exp.guide.id ?? 0,
      first_name: exp.guide.first_name ?? '',
      url: exp.guide.url ?? '',
      avatar: mapGuideAvatar(exp.guide.avatar),
      rating: exp.guide.rating ?? 0,
      rate_count: exp.guide.rate_count ?? 0,
      review_count: exp.guide.review_count ?? 0,
      avg_reaction_delay: exp.guide.avg_reaction_delay,
      type: exp.guide.type ?? exp.guide.guide_type ?? '',
      date_start_work: exp.guide.is_guide_since?.toISOString() ?? '',
      date_joined: exp.guide.date_joined?.toISOString() ?? '',
      is_licensed: exp.guide.is_licensed_guide === 'true',
      orders_count: exp.guide.orders_count_guide ?? 0,
      visitors_count: exp.guide.number_of_persons_paid ?? 0,
    },
    photos: exp.photos?.map((photo) => ({
      type: photo.type,
      thumbnail: photo.thumbnail,
      medium: photo.medium,
      thumbnail_s: photo.thumbnail,
      thumbnail_m: photo.thumbnail,
      thumbnail_l: photo.thumbnail,
      thumbnail_xl: photo.thumbnail,
    })),
    is_paid: exp.is_paid ?? false,
    isUnderSanctions: exp.is_under_sanctions,
    legal_exp_type: null,
    accommodation: null,
  };
};
